import { Controller } from "@hotwired/stimulus"

// Server-side downloads run in a background job, so a row that says
// "downloading" has to go and ask how it is doing. One poll covers every
// active row on the page; when none are left, the polling stops.
export default class extends Controller {
  connect() {
    // <main> survives a morph, so this controller connects once and never
    // again. Pressing Download morphs a busy chip into a row without any
    // reconnect, so the restart has to hang off the render events too, or
    // the very first download of a page load never gets polled.
    this.restart = () => this.#schedule()
    document.addEventListener("turbo:morph", this.restart)
    document.addEventListener("turbo:load", this.restart)
    document.addEventListener("turbo:frame-render", this.restart)
    this.#schedule()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    document.removeEventListener("turbo:morph", this.restart)
    document.removeEventListener("turbo:load", this.restart)
    document.removeEventListener("turbo:frame-render", this.restart)
  }

  #active() {
    return [...this.element.querySelectorAll("[data-download-progress]")]
      .map((el) => [el.closest("[data-episode-id]"), el])
      .filter(([row]) => row)
  }

  #schedule() {
    if (this.timer) clearTimeout(this.timer)
    if (this.#active().length === 0) return
    this.timer = setTimeout(() => this.#poll(), 1500)
  }

  async #poll() {
    const active = this.#active()
    if (active.length === 0) return
    const ids = active.map(([row]) => row.dataset.episodeId)
    try {
      const response = await fetch(`/episodes/download_status?ids=${ids.join(",")}`,
                                   { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(response.status)
      const rows = await response.json()
      let finished = false
      for (const { id, done, state, pct } of rows) {
        const match = active.find(([row]) => row.dataset.episodeId === String(id))
        if (!match) continue
        const [, chip] = match
        if (done || state === "failed" || state === null) { finished = true; continue }
        chip.style.setProperty("--pct", `${pct}%`)
        chip.dataset.tip = `Downloading ${pct}%`
        const label = chip.querySelector(".dl-pct")
        if (label) label.textContent = pct
      }
      if (finished) {
        // A finished download changes which control the row shows, so let
        // the server re-render it. Same URL, so Turbo morphs in place and
        // the scroll position holds.
        if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
        else window.location.reload()
        return
      }
    } catch (_error) {
      // Keep polling: a single failed poll is not worth killing the row's
      // only feedback over.
    }
    this.#schedule()
  }
}
